import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

export const CinemaPlayer = ({ film, onClose }) => {
  useEffect(() => {
    if (!film) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [film, onClose]);

  return (
    <AnimatePresence>
      {film && (
        <motion.div
          data-testid="cinema-player"
          data-lenis-prevent
          className="fixed inset-0 z-50 flex flex-col backdrop-blur-sm"
          style={{ backgroundColor: "rgba(13, 11, 9, 0.96)" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={onClose}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-8">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C8402A]">Сега се прожектира</p>
              <h3 className="mt-1 truncate font-serif text-xl text-[#F4F1EA] sm:text-2xl">{film.title}</h3>
            </div>
            <button
              data-testid="cinema-close-button"
              aria-label="Затвори прожекцията"
              onClick={onClose}
              className="group flex shrink-0 items-center gap-2.5 rounded-full bg-[#C8402A] py-2.5 pl-5 pr-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#F4F1EA] transition-colors duration-300 hover:bg-[#B0341F]"
            >
              Затвори
              <span className="grid h-7 w-7 place-items-center rounded-full bg-[#F4F1EA]/15">
                <X className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-90" />
              </span>
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center px-4 pb-4 sm:px-8">
            <motion.div
              className="w-full max-w-6xl"
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="relative mx-auto aspect-video overflow-hidden rounded-2xl border border-[#F4F1EA]/15 bg-black shadow-2xl"
                style={{ width: "min(100%, calc(66vh * 16 / 9))" }}
              >
                <iframe
                  key={film.id}
                  src={film.vimeo}
                  title={film.title}
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  allowFullScreen
                />
              </div>
              <div
                className="mx-auto mt-4 hidden items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-[#F4F1EA]/50 sm:flex"
                style={{ width: "min(100%, calc(66vh * 16 / 9))" }}
              >
                <span>
                  {film.year} · {film.duration} · {film.camera}
                </span>
                <span>{film.category}</span>
              </div>
            </motion.div>
          </div>

          <div className="px-4 pb-5 sm:pb-6">
            <button
              data-testid="cinema-close-bar"
              onClick={onClose}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-[#F4F1EA]/25 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F4F1EA]/85 transition-colors duration-300 hover:bg-[#F4F1EA]/10 sm:hidden"
            >
              <X className="h-4 w-4" /> Затвори прожекцията
            </button>
            <p className="hidden text-center font-mono text-[9px] uppercase tracking-[0.25em] text-[#F4F1EA]/35 sm:block">
              Кликни извън видеото или натисни Esc, за да затвориш
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
